/**
 * Bookings API service
 * Provides functions to interact with the bookings API endpoints
 */

import { api } from './api';
import { getApiBase } from '../lib/apiBase';
import type { PaginatedResponse } from './api';
import type { UnifiedBookingStatus } from '@leal-crm/shared';

/**
 * Customer data included in booking responses
 */
export interface BookingCustomer {
  id: string;
  name: string;
  email: string;
  phone: string;
}

/**
 * Vehicle data included in booking responses
 */
export interface BookingVehicle {
  id: string;
  registration: string;
  make: string;
  model: string;
  year?: number;
  color?: string;
}

/**
 * Booking entity from API
 */
export type BookingStatus = UnifiedBookingStatus;

/**
 * Staff user data included in booking responses
 */
export interface BookingCreatedBy {
  id: string;
  name: string;
}

export interface Booking {
  id: string;
  customerId: string;
  vehicleId: string;
  /** Phase 1 cases: internal long-lived container for multiple bookings/jobs */
  caseId?: string | null;
  userId: string | null;
  staffId: string | null;
  type: 'seguro' | 'particular';
  caseCategory?: 'particular' | 'seguro' | 'garantia';
  bookingType?: 'private' | 'insurance' | 'warranty';
  jobId?: string | null;
  insuranceCompany?: string | null;
  insurancePolicyNumber?: string | null;
  insuranceClaimNumber?: string | null;
  insuranceAdjusterName?: string | null;
  insuranceAdjusterPhone?: string | null;
  insuranceAdjusterEmail?: string | null;
  workflowStage?: string; // Legacy compatibility
  status?: UnifiedBookingStatus;
  bookingStatus?: BookingStatus;
  source?: 'staff' | 'public_widget' | 'marketing_link' | 'customer'; // Booking source
  scheduledAt: number;
  confirmed: boolean;
  confirmedAt?: number;
  notes?: string;
  trackingToken: string;
  bookingReference?: string; // LEAL-XXXXXX format
  createdAt: number;
  updatedAt: number;
  customer?: BookingCustomer;
  vehicle?: BookingVehicle;
  createdBy?: BookingCreatedBy; // Staff who created the booking
}

/**
 * Booking search parameters
 */
export interface BookingSearchParams {
  customerId?: string;
  vehicleId?: string;
  stage?: string;
  bookingStatus?: BookingStatus;
  startDate?: number;
  endDate?: number;
  confirmed?: boolean;
  hideUnverifiedWidget?: boolean; // When false, shows all bookings including unverified widget ones
  limit?: number;
  offset?: number;
}

/**
 * Create booking request data
 */
export interface CreateBookingData {
  customerId: string;
  vehicleId: string;
  type: 'seguro' | 'particular';
  caseCategory?: 'particular' | 'seguro' | 'garantia';
  insuranceCompany?: string;
  insurancePolicyNumber?: string;
  insuranceClaimNumber?: string;
  insuranceAdjusterName?: string;
  insuranceAdjusterPhone?: string;
  insuranceAdjusterEmail?: string;
  scheduledAt: number;
  notes?: string;
}

/**
 * Update booking request data
 */
export interface UpdateBookingData {
  scheduledAt?: number;
  notes?: string | null;
}

/**
 * Booking list response from API
 */
interface BookingListResponse {
  items: Booking[];
  limit: number;
  offset: number;
}

/**
 * Search/list bookings with optional filters
 */
export async function list(params: BookingSearchParams = {}): Promise<PaginatedResponse<Booking>> {
  const searchParams = new URLSearchParams();

  if (params.customerId) searchParams.set('customerId', params.customerId);
  if (params.vehicleId) searchParams.set('vehicleId', params.vehicleId);
  if (params.stage) searchParams.set('stage', params.stage);
  if (params.bookingStatus) searchParams.set('bookingStatus', params.bookingStatus);
  if (params.startDate) searchParams.set('startDate', params.startDate.toString());
  if (params.endDate) searchParams.set('endDate', params.endDate.toString());
  if (params.confirmed !== undefined) searchParams.set('confirmed', params.confirmed.toString());
  if (params.hideUnverifiedWidget !== undefined) searchParams.set('hideUnverifiedWidget', params.hideUnverifiedWidget.toString());
  if (params.limit) searchParams.set('limit', params.limit.toString());
  if (params.offset) searchParams.set('offset', params.offset.toString());

  const queryString = searchParams.toString();
  const endpoint = queryString ? `/api/bookings?${queryString}` : '/api/bookings';

  const response = await api.get<BookingListResponse>(endpoint);

  return {
    items: response.items,
    total: response.items.length + (response.offset || 0), // API doesn't return total yet
    limit: response.limit,
    offset: response.offset,
  };
}

/**
 * Get today's bookings
 */
export async function getToday(): Promise<Booking[]> {
  return api.get<Booking[]>('/api/bookings/today');
}

/**
 * Get booking by ID with full details
 */
export async function getById(id: string): Promise<Booking> {
  return api.get<Booking>(`/api/bookings/${id}`);
}

/**
 * Create a new booking
 */
export async function create(data: CreateBookingData): Promise<Booking> {
  return api.post<Booking>('/api/bookings', data);
}

/**
 * Update an existing booking
 */
export async function update(id: string, data: UpdateBookingData): Promise<Booking> {
  return api.patch<Booking>(`/api/bookings/${id}`, data);
}

/**
 * Confirm a booking
 */
export async function confirm(id: string): Promise<Booking> {
  return api.post<Booking>(`/api/bookings/${id}/confirm`);
}

/**
 * Cancel a booking
 */
export async function cancel(id: string, reason?: string, notes?: string): Promise<Booking> {
  return api.post<Booking>(`/api/bookings/${id}/cancel`, { reason, notes });
}

/**
 * Get unverified bookings (widget bookings pending email verification)
 */
export async function getUnverified(): Promise<Booking[]> {
  const params = new URLSearchParams({
    source: 'public_widget',
    hideUnverifiedWidget: 'false',
  });
  // Get unconfirmed widget bookings
  const result = await api.get<PaginatedResponse<Booking>>(`/api/bookings?${params.toString()}`);
  return result.items.filter((b) => !b.confirmed);
}

/**
 * Resend confirmation response
 */
export interface ResendConfirmationResponse {
  email: string;
  expiresAt: string;
}

/**
 * Resend confirmation email for a booking
 */
export async function resendConfirmation(id: string): Promise<ResendConfirmationResponse> {
  return api.post<ResendConfirmationResponse>(`/api/bookings/${id}/resend-confirmation`);
}

/**
 * Resend email notification for the current booking status
 */
export async function resendNotification(id: string): Promise<{ templateId: string; method: string }> {
  return api.post<{ templateId: string; method: string }>(`/api/bookings/${id}/resend-email`);
}

/**
 * Status history entry from repair API
 */
export interface StatusHistoryEntry {
  id: string;
  repairId: string;
  fromStage: string | null;
  toStage: string;
  staffId: string;
  notes?: string;
  createdAt: number;
}

/**
 * Repair details including status history
 */
export interface RepairDetails {
  repair: {
    id: string;
    bookingId: string;
    notes?: string;
    createdAt: number;
  };
  booking: {
    id: string;
    type: string;
    workflowStage: string;
    scheduledAt: number;
    notes?: string;
  };
  customer: {
    id: string;
    name: string;
    phone: string;
    email: string;
  } | null;
  vehicle: {
    id: string;
    registration: string;
    make: string;
    model: string;
    color?: string;
  } | null;
  photos: Array<{
    id: string;
    type: string;
    url: string;
    createdAt: number;
  }>;
  history: StatusHistoryEntry[];
  allowedTransitions: Array<{
    stage: string;
    name: string;
    color: string;
  }>;
}

/**
 * Get repair details for a booking (includes status history)
 */
export async function getRepairDetails(bookingId: string): Promise<RepairDetails> {
  return api.get<RepairDetails>(`/api/repairs/${bookingId}`);
}

/**
 * Vehicle intake checklist
 */
export interface VehicleIntakeChecklist {
  id: string;
  bookingId: string;
  exteriorPhotosCount: number;
  mileageReading?: number;
  fuelLevel?: 'empty' | 'quarter' | 'half' | 'three_quarters' | 'full';
  damageNotes?: string;
  customerSignatureUrl?: string;
  completedAt?: number;
  completedBy?: string;
  createdAt: number;
  updatedAt: number;
}

/**
 * Dropoff request data
 */
export interface DropoffData {
  mileageReading?: number;
  fuelLevel?: 'empty' | 'quarter' | 'half' | 'three_quarters' | 'full';
  damageNotes?: string;
}

export interface BookingTimelineEvent {
  id: string;
  bookingId: string;
  eventType: 'STATUS_CHANGE' | 'NOTE' | 'PHOTO' | 'ESTIMATE' | 'APPROVAL' | 'ASSIGNMENT' | 'NOTIFICATION' | 'CHECKLIST';
  previousStatus: string | null;
  newStatus: string | null;
  previousState?: string | null;
  newState?: string | null;
  actorId: string | null;
  actorRole: 'staff' | 'customer' | 'system' | null;
  publicNote: string | null;
  internalNote?: string | null;
  notes?: string | null;
  metadata: Record<string, unknown> | null;
  isPublic?: boolean;
  createdAt: number;
}

/**
 * Dropoff response
 */
export interface DropoffResponse {
  booking: Booking;
  intakeChecklist: VehicleIntakeChecklist;
}

export type ChecklistItemType = 'boolean' | 'number' | 'text' | 'photo' | 'signature' | 'select' | 'file' | 'textarea' | 'multiselect' | 'date' | 'datetime';

export interface WorkflowChecklistRequirement {
  config: {
    id: string;
    status: string;
    itemKey: string;
    itemLabel: string;
    itemType: ChecklistItemType;
    isRequired: boolean;
    displayOrder: number;
    options?: string[];
    validationRules?: Record<string, unknown>;
    isActive: boolean;
  };
  value?: boolean | number | string | Record<string, unknown> | null;
  isComplete: boolean;
  errors?: string[];
}

export async function getChecklist(bookingId: string): Promise<WorkflowChecklistRequirement[]> {
  return api.get<WorkflowChecklistRequirement[]>(`/api/bookings/${bookingId}/checklist`);
}

export async function saveChecklistValues(
  bookingId: string,
  statusId: string,
  values: Array<{ itemKey: string; value: boolean | number | string | Record<string, unknown> | null }>
): Promise<void> {
  await api.post(`/api/bookings/${bookingId}/checklist-values`, { statusId, values });
}

/**
 * Mark vehicle as dropped off (arrival)
 * Transitions from 'valoracion' to 'recepcion' and creates intake checklist
 */
export async function dropoff(id: string, data?: DropoffData): Promise<DropoffResponse> {
  return api.post<DropoffResponse>(`/api/bookings/${id}/dropoff`, data || {});
}

/**
 * Get booking timeline events
 */
export async function getTimeline(id: string): Promise<BookingTimelineEvent[]> {
  return api.get<BookingTimelineEvent[]>(`/api/bookings/${id}/timeline`);
}

/**
 * Get intake checklist for a booking
 */
export async function getIntakeChecklist(bookingId: string): Promise<VehicleIntakeChecklist> {
  return api.get<VehicleIntakeChecklist>(`/api/bookings/${bookingId}/intake-checklist`);
}

/**
 * Update intake checklist
 */
export async function updateIntakeChecklist(
  bookingId: string,
  data: Partial<Omit<VehicleIntakeChecklist, 'id' | 'bookingId' | 'createdAt' | 'updatedAt'>>
): Promise<VehicleIntakeChecklist> {
  return api.patch<VehicleIntakeChecklist>(`/api/bookings/${bookingId}/intake-checklist`, data);
}

/**
 * Complete intake checklist
 */
export async function completeIntakeChecklist(bookingId: string): Promise<VehicleIntakeChecklist> {
  return api.post<VehicleIntakeChecklist>(`/api/bookings/${bookingId}/intake-checklist/complete`);
}

/**
 * Booking image
 */
export interface BookingImage {
  id: string;
  bookingId: string;
  url: string;
  contentType: string;
  fileSize: number;
  originalFilename?: string;
  uploadedAt: number;
}

/**
 * Get all images for a booking
 */
export async function getImages(bookingId: string): Promise<BookingImage[]> {
  return api.get<BookingImage[]>(`/api/bookings/${bookingId}/images`);
}

export type WorkflowAttachmentCategory = 'photo' | 'signature' | 'file';

export interface WorkflowAttachmentWithMeta {
  id: string;
  bookingId: string | null;
  checklistItemId: string;
  checklistItemLabel: string;
  category: WorkflowAttachmentCategory;
  contentType: string;
  fileSize: number;
  uploadedAt: number;
  uploadedBy: { id: string; name: string } | null;
  url: string;
}

/**
 * Get workflow attachments for a booking
 */
export async function getAttachments(
  bookingId: string,
  category?: WorkflowAttachmentCategory
): Promise<WorkflowAttachmentWithMeta[]> {
  const search = new URLSearchParams();
  if (category) search.set('category', category);
  const qs = search.toString();
  const url = qs
    ? `/api/bookings/${bookingId}/attachments?${qs}`
    : `/api/bookings/${bookingId}/attachments`;

  return api.get<WorkflowAttachmentWithMeta[]>(url);
}

/**
 * Get image URL for a booking image
 */
export function getImageUrl(bookingId: string, imageId: string): string {
  const apiBase = getApiBase();
  return `${apiBase}/api/bookings/${bookingId}/images/${imageId}`;
}

/**
 * Delete a booking image
 */
export async function deleteImage(bookingId: string, imageId: string): Promise<void> {
  await api.delete(`/api/bookings/${bookingId}/images/${imageId}`);
}

/**
 * Tracking link data
 */
export interface TrackingLinkData {
  trackingUrl: string;
  bookingReference?: string;
  plate: string;
  legacyToken: string;
}

/**
 * Get shareable tracking link for a booking
 */
export async function getTrackingLink(bookingId: string): Promise<TrackingLinkData> {
  return api.get<TrackingLinkData>(`/api/bookings/${bookingId}/tracking-link`);
}

/**
 * Send tracking link to customer via email or SMS
 */
export async function sendTrackingLink(
  bookingId: string,
  method: 'email' | 'sms'
): Promise<{ message: string }> {
  const response = await api.post<{ success: boolean; message: string }>(
    `/api/bookings/${bookingId}/tracking-link`,
    { method }
  );
  return { message: response.message };
}

export const bookingsApi = {
  list,
  getToday,
  getById,
  getUnverified,
  create,
  update,
  confirm,
  cancel,
  resendConfirmation,
  resendNotification,
  getRepairDetails,
  dropoff,
  getTimeline,
  getChecklist,
  saveChecklistValues,
  getIntakeChecklist,
  updateIntakeChecklist,
  completeIntakeChecklist,
  getImages,
  getImageUrl,
  getAttachments,
  deleteImage,
  getTrackingLink,
  sendTrackingLink,
};
